import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHero, Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { STATS } from "../lib/site";

const VALUES = [
  {
    title: "Clarity over cleverness",
    body:
      "We optimize for the engineer who joins the team in the third year. If our architecture isn't legible without us in the room, we built the wrong thing.",
  },
  {
    title: "Outcomes over outputs",
    body:
      "Velocity dashboards are vanity. We measure ourselves on the production telemetry of the systems we ship—and the contracts we keep.",
  },
  {
    title: "Production from day one",
    body:
      "Every increment runs in a real environment, with real telemetry, against real data. POC theatre is a euphemism for waste.",
  },
  {
    title: "Honest engineering",
    body:
      "We say no to scope. We name the trade-off. We surface the risk in the second meeting, not the post-mortem.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SeoHead
        title="About"
        description="A London-based software consultancy founded in 2013. 180 engineers, designers and strategists shipping production platforms for enterprise clients across seven industries."
        path="/about"
      />
      <PageHero
        overline="About"
        title={<>A London-based consultancy, run by builders, calibrated for the long engagement.</>}
        intro="Elite Solutions Hub was founded in 2013 on a simple disagreement with the rest of the industry: the work of building enterprise software has been undersold, oversimplified, and outsourced into mediocrity. We built a different kind of firm."
        testId="about-hero"
      />

      <Section className="bg-[#050505]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="overline mb-6">Who we are</p>
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "-0.025em" }}
            >
              180 engineers, designers and strategists. Headquartered in London.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-zinc-300 text-lg leading-relaxed">
            <p>
              We were founded in London in 2013 by a small group of engineering leaders tired of
              consultancy decks that didn't survive a standup. Twelve years later, Elite Solutions
              Hub Ltd. is an independent UK firm of 180 practitioners working with enterprise
              clients across FinTech, Healthcare, E-commerce, Logistics, Education, SaaS and
              Government.
            </p>
            <p>
              Our engagements range from greenfield platforms to enterprise modernisation, AI
              integration, and embedded engineering pods that run alongside in-house teams. The
              average tenure of our senior engineers is over six years — a number we are quietly
              proud of in an industry that treats people like fungible resources.
            </p>
            <p>
              We do not have a sales team. We grow because the systems we ship continue to work,
              and the people responsible for them keep coming back.
            </p>
          </div>
        </div>
      </Section>

      <section
        data-testid="about-image"
        className="px-6 md:px-12 lg:px-16 py-16 bg-[#0A0A0C] border-y border-white/5"
      >
        <div className="aspect-[21/9] overflow-hidden bg-[#050505]">
          <img
            src="https://images.pexels.com/photos/15876898/pexels-photo-15876898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Office"
            className="w-full h-full object-cover opacity-90"
          />
        </div>
      </section>

      <Section testId="about-values">
        <p className="overline mb-6">What we believe</p>
        <h2
          className="section-title text-4xl md:text-5xl text-white font-light text-balance max-w-4xl mb-16"
        >
          Four principles we will not negotiate.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#27272A]">
          {VALUES.map((v, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="bg-[#050505] p-10"
              data-testid={`value-${i}`}
            >
              <p
                className="font-serif-display italic text-3xl font-light"
                style={{ color: "#C2533B" }}
              >
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-4 font-serif-display text-2xl text-white font-light">{v.title}</h3>
              <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <section className="border-y border-white/5 px-6 md:px-12 lg:px-16 py-20 bg-[#0A0A0C]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="border-l border-white/10 pl-6"
            >
              <p
                className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-white"
                style={{ letterSpacing: "-0.03em" }}
              >
                {s.number}
                <span style={{ color: "#C2533B" }}>{s.suffix}</span>
              </p>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "-0.025em" }}
            >
              Tell us what you're trying to build.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="about-cta">
              Start a Conversation <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
